import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { IntlShape } from 'react-intl/src/types';
import { BpIntlTypes } from './types';

const MESSAGE_VARIABLE_CHARACTER = '{';
const MESSAGE_HTML_CHARACTER = '<';

type UseBpIntlModuleOutput<T extends BpIntlTypes.IntlInput, KEY extends keyof T> = {
  [prop in KEY]: BpIntlTypes.IsFormatFun<
    T[prop]['defaultMessage'],
    typeof MESSAGE_VARIABLE_CHARACTER | typeof MESSAGE_HTML_CHARACTER
  >;
};

export function useBpIntlModule<T extends BpIntlTypes.IntlInput, KEY extends keyof T>(
  inputJson: T = {} as T
): UseBpIntlModuleOutput<T, KEY> {
  const intl = useIntl();
  const [messageDefinitions] = useState<T>(inputJson);

  return useMemo(
    () =>
      Object.entries(messageDefinitions).reduce(
        (result, [id, definition]) => ({
          ...result,
          [id]:
            definition.defaultMessage.includes(MESSAGE_VARIABLE_CHARACTER) ||
            definition.defaultMessage.includes(MESSAGE_HTML_CHARACTER)
              ? (variables?: BpIntlTypes.FormatMessageVariables) =>
                  intl.formatMessage(definition, variables as BpIntlTypes.FormatMessageVariables<string>)
              : intl.formatMessage(definition)
        }),
        {} as UseBpIntlModuleOutput<T, KEY>
      ),
    [intl, messageDefinitions]
  );
}

export function formatBpIntlMessage<T extends BpIntlTypes.IntlInput>(
  intl: IntlShape,
  input: T,
  key: keyof T,
  variables?: BpIntlTypes.FormatMessageVariables
): string {
  const target = input[key];
  // if translation is not found, return the key
  if (!target) {
    return `[${key.toString()}]`;
  }
  return intl.formatMessage(target, variables as BpIntlTypes.FormatMessageVariables<string>);
}
